import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { TimeConverterComponent } from "../../core/components/timeconverter/timeconverter.component";
import { EndpointsService } from '../../modules/admin/endpoints/endpoints.service';
import { ContractWorkSummaryRow } from '../../modules/admin/endpoints/endpoints.types';
import { Net2BeepleLocator } from '../../core/dtype/dtype.types';
import { RegularizeHourBagRequest } from '../../core/http/request.model';
import { RegularizeHourBagReply, RegularizeHourBagWithConceptsReply } from '../../core/http/reply.model';

@Component({
    selector: 'app-worker-regularize-hour-bag-dialog',
    imports: [TranslocoModule, MatButtonModule, MatIconModule, TimeConverterComponent],
    templateUrl: './worker-regularize-hour-bag-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .worker-regularize-hour-bag-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class WorkerRegularizeHourBagDialogComponent implements OnInit, OnDestroy {

    private readonly dialogRef = inject(MatDialogRef<WorkerRegularizeHourBagDialogComponent>);
    private readonly endpointsService = inject(EndpointsService);
    public data: { row: ContractWorkSummaryRow, locator: Net2BeepleLocator, responseWithConcepts: boolean } = inject(MAT_DIALOG_DATA);

    previousBagMinutes = new FormControl<number>({ value: this.data.row.previousBagMinutes, disabled: true });
    offsetBagMinutes = new FormControl<number>(this.data.row.offsetBagMinutes);
    currentBagMinutes = new FormControl<number>({ value: this.data.row.currentBagMinutes, disabled: true });
    compensationBagMinutes = new FormControl<number>(this.data.row.compensationBagMinutes);
    extraMinutes = new FormControl<number>({ value: Boolean(this.data.row.contractIsMaxLegalHours) ? this.data.row.extraMinutes : undefined, disabled: !Boolean(this.data.row.contractIsMaxLegalHours) });
    extraPlusMinutes = new FormControl<number>({ value: Boolean(this.data.row.contractIsMaxLegalHours) ? this.data.row.extraPlusMinutes : undefined, disabled: !Boolean(this.data.row.contractIsMaxLegalHours) });
    complementaryMinutes = new FormControl<number>({ value: Boolean(this.data.row.contractIsMaxLegalHours) ? undefined : this.data.row.complementaryMinutes, disabled: Boolean(this.data.row.contractIsMaxLegalHours) });
    complementaryPlusMinutes = new FormControl<number>({ value: Boolean(this.data.row.contractIsMaxLegalHours) ? undefined : this.data.row.complementaryPlusMinutes, disabled: Boolean(this.data.row.contractIsMaxLegalHours) });

    sending = signal(false);
    private unsubscribeAll = new Subject<void>();

    ngOnInit() {
        this.offsetBagMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (value ?? 0) - (this.compensationBagMinutes.value ?? 0) - (this.extraMinutes.value ?? 0) - (this.extraPlusMinutes.value ?? 0) - (this.complementaryMinutes.value ?? 0) - (this.complementaryPlusMinutes.value ?? 0)
            );
        });

        this.compensationBagMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (this.offsetBagMinutes.value ?? 0) - (value ?? 0) - (this.extraMinutes.value ?? 0) - (this.extraPlusMinutes.value ?? 0) - (this.complementaryMinutes.value ?? 0) - (this.complementaryPlusMinutes.value ?? 0)
            );
        });

        this.extraMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (this.offsetBagMinutes.value ?? 0) - (this.compensationBagMinutes.value ?? 0) - (value ?? 0) - (this.extraPlusMinutes.value ?? 0) - (this.complementaryMinutes.value ?? 0) - (this.complementaryPlusMinutes.value ?? 0)
            );
        });

        this.extraPlusMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (this.offsetBagMinutes.value ?? 0) - (this.compensationBagMinutes.value ?? 0) - (this.extraMinutes.value ?? 0) - (value ?? 0) - (this.complementaryMinutes.value ?? 0) - (this.complementaryPlusMinutes.value ?? 0)
            );
        });

        this.complementaryMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (this.offsetBagMinutes.value ?? 0) - (this.compensationBagMinutes.value ?? 0) - (this.extraMinutes.value ?? 0) - (this.extraPlusMinutes.value ?? 0) - (value ?? 0) - (this.complementaryPlusMinutes.value ?? 0)
            );
        });

        this.complementaryPlusMinutes.valueChanges.pipe(
            takeUntil(this.unsubscribeAll),
        ).subscribe(value => {
            this.currentBagMinutes.setValue(
                this.data.row.overTime + this.data.row.previousBagMinutes + (this.offsetBagMinutes.value ?? 0) - (this.compensationBagMinutes.value ?? 0) - (this.extraMinutes.value ?? 0) - (this.extraPlusMinutes.value ?? 0) - (this.complementaryMinutes.value ?? 0) - (value ?? 0)
            );
        });
    }

    ngOnDestroy() {
        this.unsubscribeAll.next();
    }

    async approve() {
        this.sending.set(true);
        const request: RegularizeHourBagRequest = {
            locator: this.data.locator,
            contractMonthlyOid: this.data.row.oid,
            compensationBagMinutes: this.compensationBagMinutes.value,
            complementaryPlusMinutes: this.complementaryPlusMinutes.value,
            complementaryMinutes: this.complementaryMinutes.value,
            extraPlusMinutes: this.extraPlusMinutes.value,
            extraMinutes: this.extraMinutes.value,
            offsetBagMinutes: this.offsetBagMinutes.value,
            responseWithConcepts: this.data.responseWithConcepts
        }
        const reply: RegularizeHourBagReply | RegularizeHourBagWithConceptsReply = await firstValueFrom(this.endpointsService.regularizeHourBag(request));
        this.sending.set(false);
        this.dialogRef.close("row" in reply ? reply.row : reply);
    }

    onComplementaryMinutesBlur() {
        if (this.complementaryMinutes.value && this.data.row.maxComplementaryMinutes && this.complementaryMinutes.value > this.data.row.maxComplementaryMinutes) {
            this.complementaryPlusMinutes.setValue(this.complementaryMinutes.value - this.data.row.maxComplementaryMinutes);
            this.complementaryMinutes.setValue(this.data.row.maxComplementaryMinutes);
        } else if (!this.complementaryMinutes.value || this.complementaryMinutes.value < this.data.row.maxComplementaryMinutes) {
            this.complementaryPlusMinutes.setValue(0);
        }
    }

    onExtraMinutesBlur() {
        if (this.extraMinutes.value && this.data.row.maxExtraMinutes && this.extraMinutes.value > this.data.row.maxExtraMinutes) {
            this.extraPlusMinutes.setValue(this.extraMinutes.value - this.data.row.maxExtraMinutes);
            this.extraMinutes.setValue(this.data.row.maxExtraMinutes);
        } else if (!this.extraMinutes.value || this.extraMinutes.value < this.data.row.maxExtraMinutes) {
            this.extraPlusMinutes.setValue(0);
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    minutesToString(minutes: number) {
        let str = minutes < 0 ? '-' : '';
        minutes = Math.abs(minutes);
        const hours = Math.floor(minutes / 60);
        minutes = minutes % 60;
        if (hours > 0) {
            str = str + String(hours) + 'h ';
        }
        if (minutes > 0) {
            str = str + String(minutes) + 'm';
        }
        if (str === '') {
            return '0m';
        } else {
            return str;
        }
    }
}
